/**
 * Best-effort foreground wake-word listener built on Web Speech.
 * Recognition is controlled by the app shell, automatically restarts with
 * backoff, and pauses while a real voice note owns the microphone.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_WAKE_WORDS = ['hey brain', 'second brain', 'wake up brain'];
const MAX_RESTART_DELAY_MS = 10_000;
const WAKE_DEBOUNCE_MS = 2_000;

export type VoiceWakeStatus = 'idle' | 'starting' | 'listening' | 'paused' | 'blocked' | 'unsupported' | 'error';

export interface UseVoiceWakeOptions {
  enabled: boolean;
  suspended?: boolean;
  wakeWords?: string[];
  onWake?: (heard: string) => void;
  onStatusChange?: (status: VoiceWakeStatus, listening: boolean, error: string | null) => void;
}

export interface UseVoiceWakeReturn {
  isSupported: boolean;
  isListening: boolean;
  status: VoiceWakeStatus;
  error: string | null;
  lastHeard: string | null;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function getSpeechRecognition(): any {
  if (typeof window === 'undefined') return null;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition || null;
}

function humanizeSpeechError(code: string) {
  if (code === 'not-allowed' || code === 'service-not-allowed') {
    return 'Microphone access is blocked. Allow microphone access in your browser settings.';
  }
  if (code === 'audio-capture') return 'No microphone is available to listen for “hey brain”.';
  if (code === 'network') return 'Speech recognition lost its connection. Retrying shortly.';
  return 'Wake-word listening stopped. Retrying shortly.';
}

export function useVoiceWake({
  enabled,
  suspended = false,
  wakeWords = DEFAULT_WAKE_WORDS,
  onWake,
  onStatusChange,
}: UseVoiceWakeOptions): UseVoiceWakeReturn {
  const isSupported = Boolean(getSpeechRecognition());
  const [isListening, setIsListening] = useState(false);
  const [status, setStatus] = useState<VoiceWakeStatus>(isSupported ? 'idle' : 'unsupported');
  const [error, setError] = useState<string | null>(null);
  const [lastHeard, setLastHeard] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const restartTimerRef = useRef<number | null>(null);
  const restartAttemptRef = useRef(0);
  const shouldRunRef = useRef(false);
  const lastWakeAtRef = useRef(0);
  const onWakeRef = useRef(onWake);
  const statusChangeRef = useRef(onStatusChange);
  const wakeWordsRef = useRef(wakeWords);
  onWakeRef.current = onWake;
  statusChangeRef.current = onStatusChange;
  wakeWordsRef.current = wakeWords;

  const publishStatus = useCallback((nextStatus: VoiceWakeStatus, listening: boolean, nextError: string | null = null) => {
    setStatus(nextStatus);
    setIsListening(listening);
    setError(nextError);
    statusChangeRef.current?.(nextStatus, listening, nextError);
  }, []);

  useEffect(() => {
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    shouldRunRef.current = enabled && !suspended && isSupported;

    if (!isSupported) {
      publishStatus('unsupported', false, 'This browser does not support wake-word listening.');
      return;
    }
    if (!enabled) {
      try { recognitionRef.current?.abort(); } catch { /* already stopped */ }
      recognitionRef.current = null;
      publishStatus('idle', false);
      return;
    }
    if (suspended) {
      try { recognitionRef.current?.abort(); } catch { /* already stopped */ }
      recognitionRef.current = null;
      publishStatus('paused', false);
      return;
    }

    let disposed = false;
    const SR = getSpeechRecognition();

    const scheduleRestart = () => {
      if (disposed || !shouldRunRef.current || restartTimerRef.current !== null) return;
      const delay = Math.min(MAX_RESTART_DELAY_MS, 350 * Math.pow(2, restartAttemptRef.current));
      restartAttemptRef.current += 1;
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        startRecognition();
      }, delay);
    };

    const startRecognition = () => {
      if (disposed || !shouldRunRef.current) return;
      try {
        const recognition = new SR();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        recognition.maxAlternatives = 1;
        recognitionRef.current = recognition;

        recognition.onstart = () => {
          restartAttemptRef.current = 0;
          publishStatus('listening', true);
        };
        recognition.onresult = (event: any) => {
          for (let index = event.resultIndex || 0; index < event.results.length; index += 1) {
            if (event.results[index].isFinal === false) continue;
            const transcript = String(event.results[index][0]?.transcript || '').toLowerCase().trim();
            if (!transcript) continue;
            setLastHeard(transcript);
            const matched = wakeWordsRef.current.some((word) => transcript.includes(word.toLowerCase().trim()));
            const now = Date.now();
            if (matched && now - lastWakeAtRef.current >= WAKE_DEBOUNCE_MS) {
              lastWakeAtRef.current = now;
              onWakeRef.current?.(transcript);
            }
          }
        };
        recognition.onerror = (event: any) => {
          const code = String(event.error || 'unknown');
          if (code === 'aborted') return;
          if (code === 'not-allowed' || code === 'service-not-allowed') {
            shouldRunRef.current = false;
            publishStatus('blocked', false, humanizeSpeechError(code));
            return;
          }
          publishStatus('error', false, humanizeSpeechError(code));
        };
        recognition.onend = () => {
          recognitionRef.current = null;
          if (shouldRunRef.current) scheduleRestart();
        };

        publishStatus('starting', false);
        recognition.start();
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'Could not start wake-word listening.';
        publishStatus('error', false, message);
        scheduleRestart();
      }
    };

    startRecognition();
    return () => {
      disposed = true;
      shouldRunRef.current = false;
      if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
      try { recognitionRef.current?.abort(); } catch { /* already stopped */ }
      recognitionRef.current = null;
    };
  }, [enabled, isSupported, publishStatus, suspended]);

  return { isSupported, isListening, status, error, lastHeard };
}
